import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Button,
  Divider,
  Snackbar,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { mockUsers } from '../data/mockData';
import { mockMentorships, mockMentorshipRequests } from '../data/mentorshipData';
import RequestMentorModal from '../components/mentorship/RequestMentorModal';

const Mentorship: React.FC = () => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedMentorId, setSelectedMentorId] = useState<string | undefined>();
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  
  const mentors = mockUsers.filter(u => u.role === 'Mentor');
  const activeMentorships = mockMentorships.filter(m => m.status === 'active');
  const pendingRequests = mockMentorshipRequests.filter(r => r.status === 'pending');

  const handleRequestMentorship = (request: any) => {
    console.log('Nueva solicitud de mentoría:', request);
    setSnackbar({ 
      open: true, 
      message: 'Solicitud de mentoría enviada exitosamente' 
    });
  };

  const handleOpenModal = (mentorId?: string) => {
    setSelectedMentorId(mentorId);
    setOpenModal(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Mentorías</Typography>
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={() => handleOpenModal()}
        >
          Buscar Mentor
        </Button>
      </Box>

      {/* Estadísticas rápidas */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 4 }}>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Mentorías Activas
            </Typography>
            <Typography variant="h4">{activeMentorships.length}</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Solicitudes Pendientes
            </Typography>
            <Typography variant="h4">{pendingRequests.length}</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Mentores Disponibles
            </Typography>
            <Typography variant="h4">{mentors.length}</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Mentores destacados */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
        Mentores Disponibles
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 3 }}>
        {mentors.map(mentor => (
          <Card key={mentor.id}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ width: 56, height: 56, mr: 2, bgcolor: 'success.main' }}>
                  {mentor.avatar}
                </Avatar>
                <Box>
                  <Typography variant="h6">{mentor.name}</Typography>
                  <Chip label={mentor.expertise} size="small" color="success" />
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              {mentor.industry && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Industria:</strong> {mentor.industry}
                </Typography>
              )}
              {mentor.availability && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Disponibilidad:</strong> {mentor.availability}
                </Typography>
              )}
              {mentor.rate && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Tarifa:</strong> ${mentor.rate}/hora
                </Typography>
              )}
              <Button
                variant="outlined"
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => handleOpenModal(mentor.id)}
              >
                Solicitar Mentoría
              </Button>
            </CardContent>
          </Card>
        ))}
      </Box>

      <RequestMentorModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSubmit={handleRequestMentorship}
        preselectedMentorId={selectedMentorId}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Mentorship;
